import { IWsMatch } from '@/app/core/websocket/models';
import { MatchIncidents } from '@/app/pages/sports/football/match-details/models';
import { CalendarService } from '@/app/services/calendar.service';
import { MatchFavoriteService } from '@/app/services/favorite';
import { CompetitionFavoritesService } from '@/app/services/favorite/competition-favorites.service';
import { GoogleTagAnalyticsService } from '@/app/services/google-tag';
import { TranslationService } from '@/app/services/translation.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { catchError, map, of } from 'rxjs';
import { EGoogleTagAnalyticsKeys } from '../../enums';
import { CompetitionApi, ICounterMatchApi, ISoccerMatchApi } from '../models';

export enum EStateMatches {
  ALL = 'ALL',
  LIVE = 'LIVE',
  FINISHED = 'FINISHED',
  SCHEDULED = 'SCHEDULED',
}

export interface ICounterMatchesApi {
  TotalMatches: number;
  LiveMatches: number;
  FinishedMatches: number;
  NextMatches: number;
}

const PAGE_SIZE = 10;

const defaultValues: ICounterMatchesApi = {
  TotalMatches: 0,
  LiveMatches: 0,
  FinishedMatches: 0,
  NextMatches: 0,
};

@Injectable({
  providedIn: 'root',
})
export class FootballMatchesService {
  private http = inject(HttpClient);
  private fCompetitionService = inject(CompetitionFavoritesService);
  private translateService = inject(TranslationService);
  private matchFavoriteService = inject(MatchFavoriteService);
  private env = environment;
  private headers = new HttpHeaders({
    Authorization: this.env.toritoTokenAuthorization,
    'Content-Type': 'application/json',
    'x-api-key': this.env.toritoApiKey,
  });
  private googleTagAnalyticsService = inject(GoogleTagAnalyticsService);

  counterMatches = signal<ICounterMatchApi>(defaultValues);
  soccerMatches = signal<CompetitionApi[]>([]);
  paginationMatches = signal<number>(0);
  totoalPageMatches = signal<number>(1);
  isLoadingSoccerMatches = signal<boolean>(false);
  isErrorSoccerMatches = signal<boolean>(false);

  // ws
  wsMatches = signal<Record<string, IWsMatch>>({});
  wsIncidents = signal<
    Record<string, { id: string; incidents: MatchIncidents }>
  >({});

  calendarService = inject(CalendarService);
  selectedDate = computed(() =>
    this.calendarService.selectedDate().replaceAll('-', '')
  );

  statusTapOption = signal<EStateMatches>(EStateMatches.ALL);

  isMoreMatches = computed(
    () => this.paginationMatches() < this.totoalPageMatches()
  );

  getSoccerMatches(page: number = 1) {
    if (page == 1) this.soccerMatches.set([]);

    const language = this.translateService.getLangLocalStorage();
    // formateDay = YYYYMMDD
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.isErrorSoccerMatches = signal<boolean>(false);
    this.isLoadingSoccerMatches.set(true);
    this.http
      .get<ISoccerMatchApi>(
        `${this.env.baseUrlWeb}/matches/scheduled/${this.selectedDate()}`,
        {
          headers: this.headers,
          params: {
            state: this.statusTapOption(),
            size: PAGE_SIZE,
            page,
            timezone,
            language,
          },
        }
      )
      .pipe(
        map((data) => {
          const { Competitions, ...rest } = data;
          return {
            ...rest,
            Competitions:
              Competitions === null
                ? []
                : Competitions.map((item) => {
                    // add favorite local
                    this.fCompetitionService.favoritesService.checkFavoriteCompetition(
                      item.Id,
                      item.IsFavorite
                    );
                    return {
                      ...item,
                      Matches: item.Matches.map((match) => {
                        this.fCompetitionService.favoritesService.checkFavoriteMatch(
                          match.Id,
                          match.IsFavorite
                        );
                        return match;
                      }),
                    };
                  }),
          };
        }),
        catchError((error) => {
          this.isLoadingSoccerMatches.set(false);
          this.soccerMatches.set([]);
          this.isErrorSoccerMatches.set(true);

          return of(null);
        })
      )
      .subscribe((data) => {
        this.isLoadingSoccerMatches.set(false);
        if (!data) return;

        if (data.Competitions !== null) {
          this.soccerMatches.update((prev) => [...prev, ...data.Competitions]);
        } else {
          this.soccerMatches.set([]);
        }
        const { Competitions, ...rest } = data;
        this.counterMatches.set({ ...rest });
        this.paginationMatches.set(page);
        this.totoalPageMatches.set(data.TotalPages);

        // google tag
        this.googleTagAnalyticsService.event(
          EGoogleTagAnalyticsKeys.VIEW_HOME,
          {
            competitions_count: Competitions.length,
            competitions: Competitions,
            live_matches_count: rest.LiveMatches,
          }
        );
      });
  }

  refreshSoccerMatches(page: number = 1) {
    const language = this.translateService.getLangLocalStorage();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.http
      .get<ISoccerMatchApi>(
        `${this.env.baseUrlWeb}/matches/scheduled/${this.selectedDate()}`,
        {
          headers: this.headers,
          params: {
            state: this.statusTapOption(),
            size: PAGE_SIZE,
            page,
            timezone,
            language,
          },
        }
      )
      .pipe(
        map((data) => {
          const { Competitions, ...rest } = data;
          return {
            ...rest,
            Competitions:
              Competitions === null
                ? []
                : Competitions.map((item) => ({
                    ...item,
                    isFavorite: this.fCompetitionService.isFavoriteCompetition(
                      item.Id
                    ),
                  })),
          };
        }),
        catchError((error) => {
          // no se toca isLoading
          this.isErrorSoccerMatches.set(true);
          return of(null);
        })
      )
      .subscribe((data) => {
        if (!data) return;

        // 🔄 Reemplazamos en lugar de concatenar
        this.soccerMatches.set(data.Competitions ?? []);
        this.counterMatches.set({ ...data });
        this.paginationMatches.set(page);
        this.totoalPageMatches.set(data.TotalPages);
      });
  }

  clearSoccerMatches() {
    this.soccerMatches.set([]);
    // this.counterMatches.set(defaultValues);
    this.paginationMatches.set(0);
  }

  handleStateTap(state: EStateMatches) {
    this.statusTapOption.set(state);
    this.clearSoccerMatches();
    this.getSoccerMatches(1);
  }

  callEndpintByDateClick() {
    this.clearSoccerMatches();
    this.getSoccerMatches(1);
  }

  setWsMatches(data: Record<string, IWsMatch>) {
    this.wsMatches.set(data);
  }

  setWsIncidents(
    data: Record<string, { id: string; incidents: MatchIncidents }>
  ) {
    this.wsIncidents.set(data);
  }
}
